// Safe Markdown-subset renderer for content bodies (What's-New Phase 1, decision
// default 2). Deliberately NOT a full Markdown engine and NOT a new dependency:
// the subset below is rendered to React ELEMENTS — no innerHTML anywhere — so raw
// HTML in a body is displayed as text and script injection is impossible by
// construction (the same reason the sanitising-renderer requirement exists).
//
// Supported: # / ## / ### headings · `-`/`*` bullet lists · paragraphs ·
// **bold** · *italic* · `code` · [text](http/https link). Links open in a new tab
// with rel=noopener; any non-http(s) scheme (javascript:, data:, …) renders as
// plain text. Everything else passes through literally.

import type { ReactNode } from 'react';
import { C, F, M } from '../ops-board/theme';

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

function renderInline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith('**')) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={key} style={{ fontFamily: M, fontSize: '0.92em', background: C.pill, borderRadius: 4, padding: '1px 4px' }}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('*')) out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    else {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      const href = lm?.[2] ?? '';
      if (lm && /^https?:\/\//i.test(href)) {
        out.push(<a key={key} href={href} target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>{lm[1]}</a>);
      } else {
        out.push(tok); // unsafe scheme or malformed → literal text, never a link
      }
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function SafeMarkdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.split(/\r?\n/);
  let para: string[] = [];
  let list: string[] = [];
  let key = 0;

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push(<p key={`p${key++}`} style={{ fontSize: 13, color: C.textDim, lineHeight: 1.55, margin: '0 0 10px' }}>{renderInline(para.join(' '), `p${key}`)}</p>);
    para = [];
  };
  const flushList = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul key={`u${key++}`} style={{ fontSize: 13, color: C.textDim, lineHeight: 1.55, margin: '0 0 10px', paddingLeft: 18 }}>
        {list.map((li, i) => <li key={i}>{renderInline(li, `u${key}-${i}`)}</li>)}
      </ul>,
    );
    list = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    const b = /^[-*]\s+(.*)$/.exec(line);
    if (h) {
      flushPara(); flushList();
      const sizes = [15, 14, 13.5];
      blocks.push(<div key={`h${key++}`} style={{ fontSize: sizes[h[1].length - 1], fontWeight: 800, color: C.text, margin: '10px 0 6px', fontFamily: F }}>{renderInline(h[2], `h${key}`)}</div>);
    } else if (b) {
      flushPara();
      list.push(b[1]);
    } else if (line.trim() === '') {
      flushPara(); flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara(); flushList();
  return <>{blocks}</>;
}
